import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  composeGrammarPaperAPI,
  deleteGrammarPaperAPI,
  downloadGrammarPaperPdfAPI,
  fetchGrammarBankTreeAPI,
  fetchGrammarPaperListAPI,
  saveGrammarPaperAPI,
} from "../grammarBankAPI";
import { COLOR_DOT_LIST, PRESET_LABEL, STYLE_LABEL } from "../paperOptions";
import "./GrammarBankPage.css";

// 문법뱅크. 쌓여 있는 문항으로 문제지를 만들고 저장하고 PDF 로 내려받는다.
//
// ## 사용자 경험을 모사이트(지인에듀)에 맞춘다
//
// 선생님은 모사이트에서 출제하던 손으로 이 탭에 들어온다. 화면 순서와 용어가 다르면
// 거기서부터 막힌다. 그래서 흐름과 이름을 그대로 가져왔다.
//
//     모사이트   내 자료 → 제목 입력 → [저장] → 문서 상세 → PDF 다운로드 → [수정하기]
//     문법뱅크   내 자료 → 제목 입력 → [저장] → 문서 상세 → PDF 다운로드 → [수정하기]
//
//     탭:  모사이트  지문 입력   | 문서 생성 설정 | PDF 출력 설정
//          문법뱅크  문법 고르기 | 문서 생성 설정 | PDF 출력 설정
//
// 'PDF 출력 설정' 의 항목 이름·순서·모양(아이콘 타일, 색상 선택기)도 모사이트와 같다.
// **다른 것은 하나뿐이다: 지문을 넣지 않는다.** 그 자리만 '문법 고르기' 이고, 안내 박스
// 첫 줄에서 그 사실을 못 박는다.
//
// ## UI 가 기존 화면과 부딪히지 않게 하는 규칙
//
// 1. 모든 클래스 이름이 `grammar-bank-` 로 시작한다(태그 선택자도 안 쓴다).
// 2. 공용 클래스를 쓰지 않고 색·크기도 이 화면 전용 변수로만 쓴다.
// 3. 조판과 PDF 는 서버가 만든다. 화면에 인쇄 규칙을 붙이지 않는다.
// 검사: `node teacher/scripts/check-css-scope.js`

const TAB_LIST = [
  { key: "pick", text: "문법 고르기" },
  { key: "option", text: "문서 생성 설정" },
  { key: "print", text: "PDF 출력 설정" },
];

const KIND_LIST = [
  { key: "question", text: "문제" },
  { key: "answer", text: "답지" },
  { key: "explanation", text: "해설" },
];

const CIRCLED_NUMBER = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];

const DEFAULT_PRINT = {
  accent: "#00A396",
  preset: "sm",
  column_count: 2,
  head_style: "1",
  show_page_number: true,
};

// 밑줄 라벨은 동그라미 글자로 찍는다. 번호(①)와 다른 글자라야 보기와 안 헷갈린다.
// **서버 조판(`api/echo/gs_grammar_paper.py`)과 같은 규칙이어야 한다.** 화면과 종이가
// 어긋나면 선생님이 화면에서 본 것과 다른 문제지를 받는다.
const LABEL_CIRCLE = "ⓐⓑⓒⓓⓔⓕⓖⓗ";
const PICK_PATTERN = /[([]([^()[\]]{1,60}\/[^()[\]]{1,60})[)\]]/g;

function labelMark(label) {
  const text = String(label || "").trim().replace(/^\(|\)$/g, "").trim();
  if (!text) return "";
  if (text.length === 1 && /[A-Ha-h]/.test(text)) {
    return LABEL_CIRCLE[text.toUpperCase().charCodeAt(0) - 65];
  }
  if (/^\d+$/.test(text) && Number(text) >= 1 && Number(text) <= LABEL_CIRCLE.length) {
    return LABEL_CIRCLE[Number(text) - 1];
  }
  return text;
}

// `(at / on)` 처럼 고르는 자리는 대괄호로 붙여 찍는다. 넷 이상이면 배열형의 단어
// 묶음이지 고르는 자리가 아니므로 그대로 둔다.
function renderPlain(text, keyPrefix) {
  const parts = [];
  let lastIndex = 0;
  let key = 0;
  PICK_PATTERN.lastIndex = 0;
  let matched = PICK_PATTERN.exec(text);

  while (matched) {
    const pieces = matched[1].split("/").map((one) => one.trim());
    if (pieces.length <= 3) {
      if (matched.index > lastIndex) parts.push(text.slice(lastIndex, matched.index));
      parts.push(
        <span key={`${keyPrefix}-pick-${key}`} className="grammar-bank-pick">
          {`[${pieces.join("/")}]`}
        </span>
      );
      key += 1;
      lastIndex = matched.index + matched[0].length;
    }
    matched = PICK_PATTERN.exec(text);
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

const LABEL_ONLY_PATTERN = /^\s*(?:[①-⑳]|\(?[A-Ea-e]\)?|\(?\d\)?)\s*$/;

// 보기가 라벨뿐이면(`A`, `(A)`) 본문의 밑줄이 곧 보기다. **라벨을 버리지 말 것.**
// 번호만 찍으면 `② ③ ④` 만 나열되어 어느 밑줄을 고르라는 것인지 알 수 없다.
function renderChoice(choice) {
  const text = String(choice || "");
  if (!LABEL_ONLY_PATTERN.test(text)) return renderStem(text);
  const mark = labelMark(text);
  return mark ? ` ${mark}` : null;
}

// 정답 표시. 정답이 둘 이상인 문항은 `answer_index_list` 가 정본이다.
function isAnswerIndex(item, index) {
  const list = item.answer_index_list;
  if (Array.isArray(list) && list.length) return list.includes(index);
  return index === item.answer_index;
}

// 본문의 `[[라벨|표현]]` 은 밑줄이고, 라벨은 밑줄 밖에 동그라미 글자로 앞에 붙는다.
function renderStem(text) {
  const parts = [];
  const pattern = /\[\[(.*?)\|(.*?)\]\]/g;
  let lastIndex = 0;
  let matched = pattern.exec(text);
  let key = 0;

  while (matched) {
    if (matched.index > lastIndex) {
      parts.push(...renderPlain(text.slice(lastIndex, matched.index), `plain-${key}`));
    }
    const [whole, label, body] = matched;
    const mark = labelMark(label);
    parts.push(
      <span key={`marker-${key}`} className="grammar-bank-marker">
        {mark ? <span className="grammar-bank-marker-label">{mark}</span> : null}
        <span className="grammar-bank-marker-text">{body}</span>
      </span>
    );
    key += 1;
    lastIndex = matched.index + whole.length;
    matched = pattern.exec(text);
  }
  if (lastIndex < text.length) {
    parts.push(...renderPlain(text.slice(lastIndex), `plain-${key}`));
  }
  return parts;
}

// 모사이트 PDF 출력 설정의 토글. 고른 쪽이 검정으로 채워진다.
function SegmentRow({ value, onChange, optionList, gold }) {
  return (
    <span className="grammar-bank-segment">
      {optionList.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            className={[
              "grammar-bank-segment-button",
              on ? "grammar-bank-segment-button--on" : "",
              on && gold ? "grammar-bank-segment-button--gold" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onChange(option.value)}
          >
            {option.text}
          </button>
        );
      })}
    </span>
  );
}

// 모사이트의 아이콘 타일. 종이 그림 아래에 이름이 붙고 고른 쪽이 검정으로 채워진다.
function TileRow({ value, onChange, optionList }) {
  return (
    <span className="grammar-bank-tile-row">
      {optionList.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={
            option.value === value
              ? "grammar-bank-tile grammar-bank-tile--on"
              : "grammar-bank-tile"
          }
          onClick={() => onChange(option.value)}
        >
          {option.art}
          <span className="grammar-bank-tile-text">{option.text}</span>
        </button>
      ))}
    </span>
  );
}

// 종이 그림. 줄 몇 개로 1열/2열과 페이지 번호 유무를 나타낸다.
function PaperArt({ columnCount, pageNumber }) {
  return (
    <span className="grammar-bank-art">
      <span className="grammar-bank-art-body">
        {[0, 1].slice(0, columnCount).map((column) => (
          <span key={column} className="grammar-bank-art-column">
            {[0, 1, 2, 3, 4].map((line) => (
              <span key={line} className="grammar-bank-art-line" />
            ))}
          </span>
        ))}
      </span>
      <span className="grammar-bank-art-foot">{pageNumber ? "1" : ""}</span>
    </span>
  );
}

function GrammarBankPage() {
  const [tree, setTree] = useState(null);
  const [treeError, setTreeError] = useState("");
  const [levelId, setLevelId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [selectedSubIdList, setSelectedSubIdList] = useState([]);
  const [tab, setTab] = useState("pick");

  const [title, setTitle] = useState("");
  const [perSub, setPerSub] = useState(2);
  const [total, setTotal] = useState(30);
  const [difficultyMin, setDifficultyMin] = useState(1);
  const [difficultyMax, setDifficultyMax] = useState(5);
  const [includeShortAnswer, setIncludeShortAnswer] = useState(true);
  const [shuffle, setShuffle] = useState(true);
  const [printOption, setPrintOption] = useState(DEFAULT_PRINT);

  const [paper, setPaper] = useState(null);
  const [composing, setComposing] = useState(false);
  const [message, setMessage] = useState("");
  const [generating, setGenerating] = useState("");

  // 저장된 문서. 저장돼 있고 편집 중이 아니면 문서 상세 화면을 그린다.
  const [paperList, setPaperList] = useState([]);
  const [savedPaper, setSavedPaper] = useState(null);
  const [editing, setEditing] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadTree = useCallback(async () => {
    try {
      const data = await fetchGrammarBankTreeAPI();
      setTree(data);
      setLevelId((previous) => previous || data.levels[0]?.level_id || "");
    } catch (error) {
      setTreeError(error.response?.data?.error || "문법뱅크를 불러오지 못했습니다.");
    }
  }, []);

  const loadPaperList = useCallback(async () => {
    try {
      const data = await fetchGrammarPaperListAPI();
      setPaperList(data.papers);
    } catch (error) {
      setPaperList([]);
    }
  }, []);

  useEffect(() => {
    loadTree();
    loadPaperList();
  }, [loadTree, loadPaperList]);

  const activeLevel = useMemo(
    () => tree?.levels.find((level) => level.level_id === levelId) || null,
    [tree, levelId]
  );

  useEffect(() => {
    if (
      activeLevel &&
      !activeLevel.categories.some((item) => item.category_id === categoryId)
    ) {
      setCategoryId(activeLevel.categories[0]?.category_id || "");
    }
  }, [activeLevel, categoryId]);

  const activeCategory = useMemo(
    () =>
      activeLevel?.categories.find(
        (category) => category.category_id === categoryId
      ) || null,
    [activeLevel, categoryId]
  );

  const countBySubId = useMemo(() => {
    const map = new Map();
    tree?.levels.forEach((level) => {
      level.categories.forEach((category) => {
        category.sub_items.forEach((subItem) => {
          map.set(subItem.sub_id, subItem.item_count);
        });
      });
    });
    return map;
  }, [tree]);

  const availableCount = useMemo(
    () =>
      selectedSubIdList.reduce(
        (sum, subId) => sum + (countBySubId.get(subId) || 0),
        0
      ),
    [selectedSubIdList, countBySubId]
  );

  const sumItemCount = (subItemList) =>
    subItemList.reduce((sum, subItem) => sum + subItem.item_count, 0);

  const levelItemCount = (level) =>
    level.categories.reduce(
      (sum, category) => sum + sumItemCount(category.sub_items),
      0
    );

  const selectedInCategory = (category) =>
    category.sub_items.filter((subItem) =>
      selectedSubIdList.includes(subItem.sub_id)
    ).length;

  const toggleSubId = (subId) => {
    setSelectedSubIdList((previous) =>
      previous.includes(subId)
        ? previous.filter((item) => item !== subId)
        : [...previous, subId]
    );
  };

  const toggleCategoryAll = (category) => {
    const idList = category.sub_items
      .filter((subItem) => subItem.item_count > 0)
      .map((subItem) => subItem.sub_id);
    const allOn =
      idList.length > 0 &&
      idList.every((subId) => selectedSubIdList.includes(subId));
    setSelectedSubIdList((previous) =>
      allOn
        ? previous.filter((subId) => !idList.includes(subId))
        : [...new Set([...previous, ...idList])]
    );
  };

  const buildConfig = (seed) => ({
    sub_id_list: selectedSubIdList,
    per_sub: perSub,
    total,
    difficulty_min: difficultyMin,
    difficulty_max: difficultyMax,
    include_short_answer: includeShortAnswer,
    shuffle,
    seed,
  });

  const runCompose = async () => {
    if (selectedSubIdList.length === 0) {
      setMessage("먼저 '문법 고르기' 에서 소분류를 고르세요.");
      setTab("pick");
      return null;
    }
    setComposing(true);
    setMessage("");
    try {
      const data = await composeGrammarPaperAPI(buildConfig(undefined));
      setPaper(data);
      if (data.stat.total < total) {
        setMessage(
          `${total}문항을 요청했지만 조건에 맞는 문항이 ${data.stat.total}개입니다.`
        );
      }
      return data;
    } catch (error) {
      setMessage(error.response?.data?.error || "출제하지 못했습니다.");
      return null;
    } finally {
      setComposing(false);
    }
  };

  // 저장. 아직 뽑은 것이 없으면 먼저 뽑고 저장한다(모사이트도 저장이 곧 확정이다).
  const save = async () => {
    if (!title.trim()) {
      setMessage("제목을 입력하세요.");
      return;
    }
    let current = paper;
    if (!current) {
      current = await runCompose();
      if (!current) {
        return;
      }
    }
    setSaving(true);
    try {
      const saved = await saveGrammarPaperAPI({
        id: savedPaper?.id,
        title: title.trim(),
        config: buildConfig(current.seed),
        print: printOption,
        item_count: current.stat.total,
      });
      setSavedPaper(saved);
      setEditing(false);
      setMessage("");
      await loadPaperList();
    } catch (error) {
      setMessage(error.response?.data?.error || "저장하지 못했습니다.");
    }
    setSaving(false);
  };

  const openPaper = async (item) => {
    const config = item.config || {};
    setSavedPaper(item);
    setTitle(item.title);
    setSelectedSubIdList(config.sub_id_list || []);
    setPerSub(config.per_sub ?? 2);
    setTotal(config.total ?? 30);
    setDifficultyMin(config.difficulty_min ?? 1);
    setDifficultyMax(config.difficulty_max ?? 5);
    setIncludeShortAnswer(config.include_short_answer !== false);
    setShuffle(config.shuffle !== false);
    setPrintOption({ ...DEFAULT_PRINT, ...(item.print || {}) });
    setEditing(false);
    setMessage("");
    setTab("pick");
    try {
      const data = await composeGrammarPaperAPI(config);
      setPaper(data);
    } catch (error) {
      setPaper(null);
    }
  };

  const startNew = () => {
    setSavedPaper(null);
    setPaper(null);
    setTitle("");
    setSelectedSubIdList([]);
    setEditing(true);
    setTab("pick");
    setMessage("");
  };

  const removePaper = async () => {
    if (!savedPaper) {
      return;
    }
    await deleteGrammarPaperAPI(savedPaper.id);
    await loadPaperList();
    startNew();
  };

  // PDF 는 서버가 굽는다. 화면에 보이는 그 문제지를 그대로 받으려고 seed 를 함께 보낸다.
  const downloadPdf = async (kind) => {
    if (!paper) {
      setMessage("문항을 다시 뽑는 중입니다. 잠시 뒤에 받으세요.");
      return;
    }
    setGenerating(kind);
    setMessage("");
    try {
      await downloadGrammarPaperPdfAPI({
        kind,
        ...buildConfig(paper.seed),
        title: title || "문법 테스트",
        ...printOption,
      });
    } catch (error) {
      let text = "PDF 를 만들지 못했습니다.";
      try {
        const parsed = JSON.parse(await error.response.data.text());
        text = parsed.error || text;
      } catch (ignored) {
        // 본문이 JSON 이 아니면 기본 문구를 쓴다.
      }
      setMessage(text);
    }
    setGenerating("");
  };

  const changePrint = (key, value) =>
    setPrintOption((previous) => ({ ...previous, [key]: value }));

  if (treeError) {
    return (
      <div className="grammar-bank">
        <div className="grammar-bank-error">{treeError}</div>
      </div>
    );
  }

  if (!tree) {
    return (
      <div className="grammar-bank">
        <div className="grammar-bank-empty">불러오는 중입니다.</div>
      </div>
    );
  }

  return (
    <div className="grammar-bank">
      <div className="grammar-bank-layout">
        <div className="grammar-bank-explorer">
          <div className="grammar-bank-explorer-head">내 자료</div>
          <button
            type="button"
            className="grammar-bank-explorer-new"
            onClick={startNew}
          >
            + 새로 만들기
          </button>
          {paperList.map((item) => (
            <button
              key={item.id}
              type="button"
              className={
                savedPaper?.id === item.id
                  ? "grammar-bank-explorer-row grammar-bank-explorer-row--on"
                  : "grammar-bank-explorer-row"
              }
              onClick={() => openPaper(item)}
            >
              <span className="grammar-bank-explorer-label">{item.title}</span>
              <span className="grammar-bank-explorer-count">
                {item.item_count}문항
              </span>
            </button>
          ))}
          {paperList.length === 0 && (
            <div className="grammar-bank-explorer-foot">
              저장한 문제지가 없습니다.
            </div>
          )}

          <div className="grammar-bank-explorer-head">대분류</div>
          {tree.levels.map((level) => (
            <button
              key={level.level_id}
              type="button"
              className={
                level.level_id === levelId
                  ? "grammar-bank-explorer-row grammar-bank-explorer-row--on"
                  : "grammar-bank-explorer-row"
              }
              onClick={() => setLevelId(level.level_id)}
            >
              <span className="grammar-bank-explorer-label">
                {level.level_label}
              </span>
              <span className="grammar-bank-explorer-count">
                ({levelItemCount(level)})
              </span>
            </button>
          ))}
        </div>

        <div className="grammar-bank-main">
          {savedPaper && !editing ? (
            <>
              <div className="grammar-bank-topbar">
                <span className="grammar-bank-crumb">내 자료 /</span>
                <span className="grammar-bank-doc-title">{savedPaper.title}</span>
                <button
                  type="button"
                  className="grammar-bank-button"
                  onClick={removePaper}
                >
                  휴지통으로 이동
                </button>
                <button
                  type="button"
                  className="grammar-bank-solid-button"
                  onClick={() => setEditing(true)}
                >
                  수정하기
                </button>
              </div>

              <div className="grammar-bank-meta">
                #{savedPaper.id} 문법 문제지 · 문항 {savedPaper.item_count}개 ·{" "}
                {new Date(savedPaper.updated_at).toLocaleString("ko-KR")}
                <span className="grammar-bank-badge">제작 완료</span>
              </div>

              <div className="grammar-bank-download-head">다운로드</div>
              <div className="grammar-bank-download-list">
                {KIND_LIST.map((kind) => (
                  <div key={kind.key} className="grammar-bank-download-row">
                    <span className="grammar-bank-download-icon">PDF</span>
                    <span className="grammar-bank-download-name">
                      {kind.text}_{savedPaper.title}.pdf
                    </span>
                    <button
                      type="button"
                      className="grammar-bank-button"
                      onClick={() => downloadPdf(kind.key)}
                      disabled={Boolean(generating) || !paper}
                    >
                      {generating === kind.key ? "만드는 중..." : "다운로드"}
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="grammar-bank-topbar">
                <span className="grammar-bank-crumb">내 자료 /</span>
                <input
                  type="text"
                  className="grammar-bank-title-input"
                  value={title}
                  placeholder="제목을 입력하세요."
                  onChange={(event) => setTitle(event.target.value)}
                />
                <button
                  type="button"
                  className="grammar-bank-solid-button"
                  onClick={save}
                  disabled={saving || composing}
                >
                  {saving || composing ? "저장 중..." : "저장"}
                </button>
              </div>

              <div className="grammar-bank-meta">
                문법뱅크 · 고른 소분류 {selectedSubIdList.length}개 · 지금 고른
                것으로 최대 {availableCount}문항
              </div>

              <div className="grammar-bank-steps">
                <span
                  className={
                    savedPaper
                      ? "grammar-bank-step"
                      : "grammar-bank-step grammar-bank-step--on"
                  }
                >
                  <span className="grammar-bank-step-number">1</span>내용 입력 후
                  저장
                </span>
                <span className="grammar-bank-step-arrow">→</span>
                <span
                  className={
                    savedPaper
                      ? "grammar-bank-step grammar-bank-step--on"
                      : "grammar-bank-step"
                  }
                >
                  <span className="grammar-bank-step-number">2</span>다음 화면에서
                  PDF 다운로드
                </span>
                <span className="grammar-bank-step-note">(포인트 차감 없음)</span>
              </div>

              <div className="grammar-bank-tab-row">
                {TAB_LIST.map((tabItem) => (
                  <button
                    key={tabItem.key}
                    type="button"
                    className={
                      tab === tabItem.key
                        ? "grammar-bank-tab grammar-bank-tab--on"
                        : "grammar-bank-tab"
                    }
                    onClick={() => setTab(tabItem.key)}
                  >
                    {tabItem.text}
                  </button>
                ))}
              </div>

              {tab === "pick" && (
                <>
                  <div className="grammar-bank-notice">
                    <div className="grammar-bank-notice-head">안내</div>
                    <ul className="grammar-bank-notice-list">
                      <li className="grammar-bank-notice-line">
                        이 탭은 <b>지문을 넣지 않습니다.</b> 이미 만들어 둔 문항에서
                        골라 뽑습니다.
                      </li>
                      <li className="grammar-bank-notice-line">
                        왼쪽 <b>대분류</b>에서 레벨을 고르고, 가운데 <b>중분류</b>를
                        고른 뒤, 오른쪽에서 <b>소분류</b>를 체크하세요.
                      </li>
                    </ul>
                  </div>

                  <div className="grammar-bank-pick">
                    <div className="grammar-bank-pick-pane">
                      <div className="grammar-bank-pick-head">중분류</div>
                      <div className="grammar-bank-pick-box">
                        {activeLevel?.categories.map((category) => {
                          const chosen = selectedInCategory(category);
                          return (
                            <button
                              key={category.category_id}
                              type="button"
                              className={
                                category.category_id === categoryId
                                  ? "grammar-bank-pick-row grammar-bank-pick-row--on"
                                  : "grammar-bank-pick-row"
                              }
                              onClick={() => setCategoryId(category.category_id)}
                            >
                              <span className="grammar-bank-pick-label">
                                {category.category_label}
                              </span>
                              {chosen > 0 && (
                                <span className="grammar-bank-pick-chosen">
                                  {chosen}
                                </span>
                              )}
                              <span className="grammar-bank-pick-count">
                                {sumItemCount(category.sub_items)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grammar-bank-pick-pane grammar-bank-pick-pane--wide">
                      <div className="grammar-bank-pick-head">
                        소분류
                        {activeCategory && (
                          <button
                            type="button"
                            className="grammar-bank-link-button"
                            onClick={() => toggleCategoryAll(activeCategory)}
                          >
                            전체
                          </button>
                        )}
                      </div>
                      <div className="grammar-bank-pick-box">
                        {activeCategory?.sub_items.map((subItem) => (
                          <label
                            key={subItem.sub_id}
                            className={
                              subItem.item_count === 0
                                ? "grammar-bank-sub-row grammar-bank-sub-row--empty"
                                : "grammar-bank-sub-row"
                            }
                          >
                            <input
                              type="checkbox"
                              className="grammar-bank-checkbox"
                              disabled={subItem.item_count === 0}
                              checked={selectedSubIdList.includes(subItem.sub_id)}
                              onChange={() => toggleSubId(subItem.sub_id)}
                            />
                            <span className="grammar-bank-sub-label">
                              {subItem.sub_label}
                            </span>
                            <span className="grammar-bank-sub-count">
                              {subItem.item_count}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {tab === "option" && (
                <>
                  <div className="grammar-bank-notice">
                    <div className="grammar-bank-notice-head">안내</div>
                    <div className="grammar-bank-notice-line">
                      낼 문항 수와 난이도를 정하세요. 기본값 그대로 저장해도 됩니다.
                    </div>
                  </div>

                  <div className="grammar-bank-setting-box">
                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">
                        소분류당 최대
                      </span>
                      <span className="grammar-bank-setting-control">
                        <input
                          type="number"
                          className="grammar-bank-input grammar-bank-input--number"
                          min="0"
                          value={perSub}
                          onChange={(event) =>
                            setPerSub(Number(event.target.value))
                          }
                        />
                        <span className="grammar-bank-setting-hint">
                          낮추면 여러 문법을 고르게, 높이면 한 문법을 깊게 묻습니다
                        </span>
                      </span>
                    </div>
                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">총 문항</span>
                      <span className="grammar-bank-setting-control">
                        <input
                          type="number"
                          className="grammar-bank-input grammar-bank-input--number"
                          min="1"
                          value={total}
                          onChange={(event) =>
                            setTotal(Number(event.target.value))
                          }
                        />
                        <span className="grammar-bank-setting-hint">
                          지금 고른 것으로 최대 {availableCount}문항
                        </span>
                      </span>
                    </div>
                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">난이도</span>
                      <span className="grammar-bank-setting-control">
                        <input
                          type="number"
                          className="grammar-bank-input grammar-bank-input--number"
                          min="1"
                          max="5"
                          value={difficultyMin}
                          onChange={(event) =>
                            setDifficultyMin(Number(event.target.value))
                          }
                        />
                        <span className="grammar-bank-setting-hint">~</span>
                        <input
                          type="number"
                          className="grammar-bank-input grammar-bank-input--number"
                          min="1"
                          max="5"
                          value={difficultyMax}
                          onChange={(event) =>
                            setDifficultyMax(Number(event.target.value))
                          }
                        />
                        <span className="grammar-bank-setting-hint">
                          1(쉬움) ~ 5(어려움)
                        </span>
                      </span>
                    </div>
                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">주관식</span>
                      <span className="grammar-bank-setting-control">
                        <SegmentRow
                          value={includeShortAnswer}
                          onChange={setIncludeShortAnswer}
                          optionList={[
                            { value: true, text: "포함" },
                            { value: false, text: "제외" },
                          ]}
                        />
                      </span>
                    </div>
                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">문항 순서</span>
                      <span className="grammar-bank-setting-control">
                        <SegmentRow
                          value={shuffle}
                          onChange={setShuffle}
                          optionList={[
                            { value: true, text: "섞기" },
                            { value: false, text: "소분류 순서" },
                          ]}
                        />
                      </span>
                    </div>
                  </div>
                </>
              )}

              {tab === "print" && (
                <>
                  <div className="grammar-bank-notice">
                    <div className="grammar-bank-notice-head">안내</div>
                    <div className="grammar-bank-notice-line">
                      이 설정은 문서를 저장한 뒤에도 편집 화면에서 언제든지 변경할 수
                      있습니다.
                    </div>
                  </div>

                  <div className="grammar-bank-setting-box">
                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">
                        페이지 번호 출력 여부
                      </span>
                      <span className="grammar-bank-setting-control">
                        <TileRow
                          value={printOption.show_page_number}
                          onChange={(value) =>
                            changePrint("show_page_number", value)
                          }
                          optionList={[
                            {
                              value: true,
                              text: "출력하기",
                              art: <PaperArt columnCount={1} pageNumber />,
                            },
                            {
                              value: false,
                              text: "숨기기",
                              art: (
                                <PaperArt columnCount={1} pageNumber={false} />
                              ),
                            },
                          ]}
                        />
                      </span>
                    </div>

                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">
                        페이지 레이아웃
                      </span>
                      <span className="grammar-bank-setting-control">
                        <TileRow
                          value={printOption.column_count}
                          onChange={(value) => changePrint("column_count", value)}
                          optionList={[
                            {
                              value: 1,
                              text: "1열",
                              art: (
                                <PaperArt columnCount={1} pageNumber={false} />
                              ),
                            },
                            {
                              value: 2,
                              text: "2열",
                              art: (
                                <PaperArt columnCount={2} pageNumber={false} />
                              ),
                            },
                          ]}
                        />
                      </span>
                    </div>

                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">글씨 크기</span>
                      <span className="grammar-bank-setting-control">
                        <span className="grammar-bank-tile-row">
                          {Object.keys(PRESET_LABEL).map((key, index) => (
                            <button
                              key={key}
                              type="button"
                              className={
                                printOption.preset === key
                                  ? "grammar-bank-tile grammar-bank-tile--on"
                                  : "grammar-bank-tile"
                              }
                              onClick={() => changePrint("preset", key)}
                            >
                              <span
                                className="grammar-bank-size-glyph"
                                style={{ fontSize: `${14 + index * 5}px` }}
                              >
                                가
                              </span>
                              <span className="grammar-bank-tile-text">
                                {PRESET_LABEL[key]}
                              </span>
                            </button>
                          ))}
                        </span>
                      </span>
                    </div>

                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">
                        레이아웃 스타일
                      </span>
                      <span className="grammar-bank-setting-control">
                        <SegmentRow
                          gold
                          value={printOption.head_style}
                          onChange={(value) => changePrint("head_style", value)}
                          optionList={Object.keys(STYLE_LABEL).map((key) => ({
                            value: key,
                            text: STYLE_LABEL[key],
                          }))}
                        />
                        <span className="grammar-bank-style-note">
                          ✨ 머리글 모양이 바뀝니다. 하나씩 눌러보세요.
                        </span>
                      </span>
                    </div>

                    <div className="grammar-bank-setting-row">
                      <span className="grammar-bank-setting-label">테마 색상</span>
                      <span className="grammar-bank-setting-control">
                        <input
                          type="text"
                          className="grammar-bank-input grammar-bank-input--color"
                          value={printOption.accent}
                          onChange={(event) =>
                            changePrint("accent", event.target.value)
                          }
                        />
                        <input
                          type="color"
                          className="grammar-bank-color-input"
                          value={printOption.accent}
                          onChange={(event) =>
                            changePrint("accent", event.target.value)
                          }
                        />
                        <span className="grammar-bank-dot-row">
                          {COLOR_DOT_LIST.map((color) => (
                            <button
                              key={color}
                              type="button"
                              className={
                                printOption.accent.toLowerCase() ===
                                color.toLowerCase()
                                  ? "grammar-bank-dot grammar-bank-dot--on"
                                  : "grammar-bank-dot"
                              }
                              style={{ background: color }}
                              onClick={() => changePrint("accent", color)}
                            />
                          ))}
                        </span>
                      </span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {message && <div className="grammar-bank-message">{message}</div>}

          {paper && (
            <>
              <div className="grammar-bank-stat">
                <span className="grammar-bank-stat-item">
                  문항 <b>{paper.stat.total}</b>
                </span>
                <span className="grammar-bank-stat-item">
                  소분류 {paper.stat.sub_used}/{paper.stat.sub_requested}
                </span>
                <span className="grammar-bank-stat-item">
                  유형{" "}
                  {Object.entries(paper.stat.by_type)
                    .sort((a, b) => b[1] - a[1])
                    .map(([type, count]) => `${type} ${count}`)
                    .join(" · ")}
                </span>
                <span className="grammar-bank-stat-item">seed {paper.seed}</span>
                {editing && (
                  <button
                    type="button"
                    className="grammar-bank-link-button"
                    onClick={() => runCompose()}
                    disabled={composing}
                  >
                    다시 뽑기
                  </button>
                )}
              </div>

              <div className="grammar-bank-paper">
                <div className="grammar-bank-paper-head">
                  <span className="grammar-bank-paper-title">
                    {title || "문법 테스트"}
                  </span>
                  <span className="grammar-bank-paper-kind">미리보기</span>
                </div>

                {paper.items.map((item) => (
                  <div key={item.item_id} className="grammar-bank-item">
                    <div className="grammar-bank-item-head">
                      <span className="grammar-bank-item-number">
                        {item.number}
                      </span>
                      <span className="grammar-bank-item-instruction">
                        {item.instruction}
                      </span>
                      <span className="grammar-bank-item-meta">
                        {item.sub_label} · {item.item_type} · 난이도{" "}
                        {item.difficulty}
                      </span>
                    </div>

                    {/* 영작형은 우리말이 물음 자체다. 빠지면 무엇을 쓰라는 것인지 알 수 없다. */}
                    {item.kr && (
                      <div className="grammar-bank-item-kr">{item.kr}</div>
                    )}

                    {item.stem && (
                      <div className="grammar-bank-item-stem">
                        {renderStem(item.stem)}
                      </div>
                    )}

                    {Array.isArray(item.condition_list) && item.condition_list.length > 0 && (
                      <div className="grammar-bank-item-aside">
                        <b>조건</b>
                        {item.condition_list.map((one, index) => (
                          <div key={`cond-${item.item_id}-${index}`}>· {one}</div>
                        ))}
                      </div>
                    )}

                    {Array.isArray(item.word_bank) && item.word_bank.length > 0 && (
                      <div className="grammar-bank-item-aside">
                        <b>보기</b>
                        <div>{item.word_bank.join(", ")}</div>
                      </div>
                    )}

                    {item.choices.length > 0 && (
                      <div className="grammar-bank-choice-list">
                        {item.choices.map((choice, index) => (
                          <span
                            key={`${item.item_id}-${index}`}
                            className={
                              isAnswerIndex(item, index)
                                ? "grammar-bank-choice grammar-bank-choice--answer"
                                : "grammar-bank-choice"
                            }
                          >
                            <span className="grammar-bank-choice-number">
                              {CIRCLED_NUMBER[index] || index + 1}
                            </span>
                            {renderChoice(choice)}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="grammar-bank-item-answer">
                      정답: {item.answer_text}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GrammarBankPage;
